import React from "react";
import _ from "lodash";
import ExpressionInput from "components/common/ExpressionInput";
import SelectedStickyFooter from "components/common/SelectedStickyFooter";
import GeneralStatusIndicator from "components/common/GeneralStatusIndicator";
import Checkbox from "components/common/Checkbox";

import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { tdClass, thClass, inputClass } from "helpers/StyleClass";

const SEARCH_TYPE = {
  FUZZY: "fuzzy",
  EXACT: "exact",
};

const selectedLabel =
  "border rounded bg-blue-200 font-semibold px-2 py-1 text-xs text-blue-800 cursor-pointer";
const unselectedLabel =
  "border rounded bg-gray-100 font-semibold px-2 py-1 text-xs text-gray-800 cursor-pointer";
const disabledClass =
  "px-2 py-1 rounded text-xs bg-gray-300 text-gray-500 cursor-not-allowed";

const selectedClass = "rounded cursor-pointer px-2 bg-blue-200 text-blue-800";
const unselectedClass =
  "rounded cursor-pointer px-2 bg-gray-200 text-gray-700 hover:bg-blue-100 hover:text-blue-800";

class TargetTableView extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedSearchType: SEARCH_TYPE.FUZZY,
      selectedSearchKey: {
        [SEARCH_TYPE.FUZZY]: [],
        [SEARCH_TYPE.EXACT]: [],
      },
      inputValue: "",
      priceExpr: null,
      expErrMsg: "",

      options: {},
      selected: {},

      selectedItems: [],
      footerKey: "",
      withQuotes: false,

      exactOptions: [],
      searchOptions: [],
      footerOptions: [],

      sortKey: "",
      sortOrder: "asc", // or "desc"

      resultData: [],
      visibleCount: 200,
    };

    this.scrollRef = React.createRef();

    this.handleSelectSearchType = this.handleSelectSearchType.bind(this);
    this.handleSelectSearchKey = this.handleSelectSearchKey.bind(this);
    this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
    this.handleSelectOption = this.handleSelectOption.bind(this);
    this.handlePriceExprChange = this.handlePriceExprChange.bind(this);
    this.handlePriceExprError = this.handlePriceExprError.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);

    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleFooterSelectedByKey = this.handleFooterSelectedByKey.bind(this);
    this.handleFilterSortData = this.handleFilterSortData.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    const {
      data,
      optKey,
      optDefault = {},
      selected = [],
      columns,
      searchKey = ["id", "name"],
      exactKey = ["id", "name"],
      footerKey = ["id", "name"],
    } = this.props;

    const options = _.fromPairs(
      optKey.map((key) => [
        key,
        optDefault[key]
          ? optDefault[key].map((d) => ({
              value: d,
              label: d,
              disabled: !_.some(data, (item) => item[key] === d),
            }))
          : _.orderBy(
              _.keys(_.groupBy(data, key)).map((d) => ({
                value: d,
                label: d,
              })),
              ["label"],
              ["asc"]
            ),
      ])
    );

    const filteredOptions = _.omitBy(
      options,
      (opts, key) => !optDefault[key] && opts.length === 1
    );

    function parseOption(columns, keyList) {
      return _.map(keyList, (key) => _.find(columns, { key }))
        .filter((item) => item)
        .map((item) => ({
          value: item.key,
          label: item.label,
        }));
    }

    this.setState(
      {
        options: filteredOptions,
        selected: _.mapValues(filteredOptions, () => []),
        selectedItems: selected
          .map((id) => _.find(data, { id }))
          .filter((d) => !_.isNil(d)),

        exactOptions: parseOption(columns, exactKey),
        searchOptions: parseOption(columns, searchKey),
        footerOptions: parseOption(columns, footerKey),
        selectedSearchKey: {
          [SEARCH_TYPE.FUZZY]: searchKey,
          [SEARCH_TYPE.EXACT]: [exactKey[0]],
        },
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.selected, this.props.selected)) {
      this.setState({
        selectedItems: this.props.selected
          .map((id) => _.find(this.props.data, { id }))
          .filter((d) => !_.isNil(d)),
      });
    }
  }

  handleSelectSearchType(type) {
    this.setState({ selectedSearchType: type }, () => {
      this.handleFilterSortData();
    });
  }

  handleSelectSearchKey(key) {
    const { selectedSearchType, selectedSearchKey } = this.state;
    const parseSearchKey = selectedSearchKey[selectedSearchType];
    const newSelectedSearchKey =
      selectedSearchType === SEARCH_TYPE.FUZZY
        ? _.xor(parseSearchKey, [key])
        : [key];

    this.setState(
      {
        selectedSearchKey: {
          ...selectedSearchKey,
          [selectedSearchType]: newSelectedSearchKey,
        },
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  handleSearchInputChange(e) {
    this.setState(
      {
        inputValue: e.target.value,
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  handleSelectOption(key, value) {
    const { selected } = this.state;

    const isSelected = selected[key].includes(value);
    this.setState(
      {
        selected: {
          ...selected,
          [key]: isSelected
            ? selected[key].filter((v) => v !== value)
            : [...selected[key], value],
        },
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  handlePriceExprChange(expr) {
    this.setState(
      {
        priceExpr: expr,
        expErrMsg: "",
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  handlePriceExprError(error) {
    this.setState(
      {
        expErrMsg: error.message,
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  handleSortChange({ sortKey, sortOrder }) {
    this.setState(
      {
        sortKey,
        sortOrder,
      },
      () => {
        this.handleFilterSortData();
      }
    );
  }

  handleSelectAll(list) {
    const { onSelect } = this.props;

    this.setState({
      selectedItems: list,
    });

    if (onSelect) {
      onSelect(list.map((item) => item.id));
    }
  }

  handleSelect(id) {
    const { data, onSelect } = this.props;
    const { selectedItems } = this.state;
    const isSelected = _.find(selectedItems, { id });

    const newList = isSelected
      ? selectedItems.filter((item) => item.id !== id)
      : [...selectedItems, _.find(data, { id })];

    this.setState({
      selectedItems: newList,
    });

    if (onSelect) {
      onSelect(newList.map((item) => item.id));
    }
  }

  handleFooterSelectedByKey(key) {
    this.setState({
      footerKey: key,
    });
  }

  handleFilterSortData() {
    const { data } = this.props;

    const {
      inputValue,
      selectedSearchKey,
      selectedSearchType,
      priceExpr,
      expErrMsg,
      sortKey,
      sortOrder,
      options,
      selected,
    } = this.state;

    if (this.scrollRef.current) this.scrollRef.current.scrollTop = 0;

    const filteredData =
      expErrMsg !== ""
        ? []
        : data.filter((item) => {
            // Search by input value
            const searchText = _.trim(inputValue.toLowerCase());
            const searchTextList = searchText.split(",").map((d) => d.trim());
            const parseSearchKey = selectedSearchKey[selectedSearchType];
            const matchesSearch =
              searchText === "" ||
              (selectedSearchType === SEARCH_TYPE.FUZZY
                ? _.some(parseSearchKey, (key) => {
                    return (item[key] || "")
                      .toString()
                      .toLowerCase()
                      .includes(searchText);
                  })
                : searchTextList.includes(
                    (item[parseSearchKey[0]] || "").toString().toLowerCase()
                  ));
            if (!matchesSearch) return false;

            // Filter by price expression
            let matchPrice = !priceExpr;
            if (priceExpr && !_.isNil(item.price)) {
              try {
                matchPrice = priceExpr.evaluate({ ...item });
              } catch (e) {}
            }

            if (!matchPrice) return false;

            // Filter by selected options
            const matchOptions = _.keys(options).every((optKey) => {
              const selectedValues = selected[optKey] || [];
              return (
                selectedValues.length === 0 ||
                selectedValues.includes(item[optKey])
              );
            });

            return matchOptions;
          });

    // Sort the filtered data
    const sortedData = _.orderBy(filteredData, sortKey ? [sortKey] : [], [
      sortOrder,
    ]);

    this.setState({
      resultData: sortedData,
      visibleCount: Math.min(sortedData.length, 200),
    });
  }

  handleScroll() {
    const { resultData } = this.state;
    const el = this.scrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      this.setState((prev) => ({
        visibleCount: Math.min(prev.visibleCount + 50, resultData.length),
      }));
    }
  }

  render() {
    const { data, columns, sortList, isShowFooter = true } = this.props;

    const {
      resultData,
      options,
      selected,
      selectedSearchType,
      selectedSearchKey,
      inputValue,
      sortKey,
      sortOrder,
      selectedItems,
      footerKey,
      withQuotes,
      visibleCount,
      expErrMsg,

      footerOptions,
      searchOptions,
      exactOptions,
    } = this.state;

    // Check isSelectedAll
    const selectedById = _.map(selectedItems, (item) => item.id);
    const sortedById = _.map(resultData, (item) => item.id);

    const isSelectedAll =
      _.xor(selectedById, sortedById).length === 0 && selectedById.length > 0;

    // Get footer items based on footerKey
    const footerByKey = _.map(selectedItems, (item) =>
      withQuotes ? `"${item[footerKey]}"` : item[footerKey]
    );

    // Get placeholder for input
    const parseSearchKey = selectedSearchKey[selectedSearchType];
    const optionsSearchKey = _.map(parseSearchKey, (key) =>
      _.find(
        selectedSearchType === SEARCH_TYPE.FUZZY ? searchOptions : exactOptions,
        { value: key }
      )
    ).filter((item) => item);
    const placeholder = optionsSearchKey
      .map((d) => d.label)
      .join(selectedSearchType === SEARCH_TYPE.FUZZY ? " or " : "");
    const inputPlaceholder =
      selectedSearchType === SEARCH_TYPE.EXACT
        ? `Exact match by ${placeholder}, please use comma to separate.`
        : `Fuzzy search by ${placeholder}.`;

    // Get visible data from resultData and visibleCount
    const visibleData = resultData.slice(0, visibleCount);

    return (
      <div className="flex h-full gap-2">
        <div
          className="border-r flex flex-col gap-3 overflow-y-auto pr-2"
          style={{ width: "340px" }}
        >
          <div className="border-b flex flex-col gap-1 pb-3 text-sm">
            <div className="flex gap-1">
              {[SEARCH_TYPE.FUZZY, SEARCH_TYPE.EXACT].map((type) => (
                <div
                  className={`${
                    selectedSearchType === type
                      ? selectedClass
                      : unselectedClass
                  }`}
                  onClick={() => this.handleSelectSearchType(type)}
                  key={type}
                >
                  {type === SEARCH_TYPE.FUZZY ? "Fuzzy search" : "Exact match"}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {(selectedSearchType === SEARCH_TYPE.EXACT
                ? exactOptions
                : searchOptions
              ).map((item) => (
                <div
                  key={item.value}
                  className={`${
                    selectedSearchKey[selectedSearchType].includes(item.value)
                      ? selectedClass
                      : unselectedClass
                  }`}
                  onClick={() => this.handleSelectSearchKey(item.value)}
                >
                  {item.label}
                </div>
              ))}
            </div>
            <input
              type="text"
              className={inputClass}
              placeholder={inputPlaceholder}
              onChange={this.handleSearchInputChange}
              value={inputValue}
            ></input>
          </div>

          {_.keys(options).map((optKey) => (
            <div key={optKey} className="flex items-start gap-2">
              <div
                className="pt-1 text-sm text-gray-600"
                style={{ width: optKey.length >= 8 ? "auto" : "3.5rem" }}
              >
                {_.startCase(optKey)}
              </div>
              {optKey === "price" ? (
                <div>
                  <ExpressionInput
                    placeholder="Enter expression to filter price"
                    onChange={this.handlePriceExprChange}
                    onError={this.handlePriceExprError}
                  ></ExpressionInput>
                  {expErrMsg !== "" && (
                    <div className="text-xs text-red-600">{expErrMsg}</div>
                  )}
                </div>
              ) : (
                <div className="flex flex-1 flex-wrap gap-1">
                  {options[optKey].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={
                        opt.disabled
                          ? disabledClass
                          : selected[optKey].includes(opt.value)
                          ? selectedLabel
                          : unselectedLabel
                      }
                      onClick={() => {
                        if (!opt.disabled) {
                          this.handleSelectOption(optKey, opt.value);
                        }
                      }}
                    >
                      {optKey === "subGroup" && opt.label === ""
                        ? "None"
                        : opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div
          className="flex flex-col gap-2"
          style={{ width: "calc(100% - 340px - 0.5rem)" }}
        >
          <div className="text-sm font-semibold text-gray-600">
            {resultData.length} / {data.length} item(s)
          </div>
          <div
            ref={this.scrollRef}
            onScroll={this.handleScroll}
            className="flex-1 overflow-y-auto"
          >
            <table className="table w-full table-auto text-sm">
              <thead className="bg-gray-200 text-xs">
                <tr>
                  <th className={thClass + " w-4"}>
                    <Checkbox
                      isChecked={isSelectedAll}
                      onChange={() =>
                        this.handleSelectAll(isSelectedAll ? [] : resultData)
                      }
                    ></Checkbox>
                  </th>
                  {columns.map((col) => {
                    let parsedThClass = thClass;
                    if (col.key === "status") parsedThClass += " w-20";
                    else parsedThClass += ` ${col.class || ""}`;
                    return (
                      <th key={col.key} className={parsedThClass}>
                        <div className="flex items-center">
                          <span className="select-none text-gray-600">
                            {col.label}
                          </span>
                          {sortList && sortList.includes(col.key) && (
                            <button
                              type="button"
                              className="focus:outline-none relative px-2 text-sm text-gray-500"
                              onClick={() => {
                                let newOrder = "asc";
                                let newKey = col.key;
                                if (sortKey === col.key) {
                                  if (sortOrder === "asc") {
                                    newOrder = "desc";
                                  } else {
                                    newKey = "";
                                  }
                                }

                                this.handleSortChange({
                                  sortKey: newKey,
                                  sortOrder: newOrder,
                                });
                              }}
                            >
                              <FaSort />
                              {sortKey === col.key && (
                                <div
                                  className="absolute top-0 text-gray-800"
                                  style={{ left: "0.5rem" }}
                                >
                                  {sortOrder === "asc" ? (
                                    <FaSortUp />
                                  ) : (
                                    <FaSortDown />
                                  )}
                                </div>
                              )}
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleData.map((item) => {
                  return (
                    <tr
                      key={item.id}
                      className="border-b cursor-pointer hover:bg-gray-100"
                      onClick={() => this.handleSelect(item.id)}
                    >
                      <td className={tdClass}>
                        <Checkbox
                          isChecked={selectedById.includes(item.id)}
                          onChange={() => this.handleSelect(item.id)}
                        ></Checkbox>
                      </td>
                      {columns.map((col) => {
                        let content = item[col.key];
                        if (col.key === "status")
                          content = (
                            <div className="flex items-center justify-center">
                              <GeneralStatusIndicator
                                status={item.status}
                              ></GeneralStatusIndicator>
                            </div>
                          );
                        else if (col.content) {
                          content = col.content(item);
                        }
                        return (
                          <td key={col.key} className={tdClass}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {isShowFooter && footerOptions.length > 0 && (
            <SelectedStickyFooter
              idList={footerOptions}
              selectedItems={footerByKey}
              handleSelectedByKey={this.handleFooterSelectedByKey}
              handleClear={() => this.handleSelectAll([])}
              position={"relative"}
              actionButtons={
                <div className="rounded flex items-center gap-2 bg-gray-100 px-2 py-1">
                  <Checkbox
                    id="double-quotes"
                    isChecked={withQuotes}
                    onChange={() => this.setState({ withQuotes: !withQuotes })}
                  ></Checkbox>
                  <label
                    htmlFor="double-quotes"
                    className="cursor-pointer text-xs text-gray-600"
                  >
                    With Double Quotes
                  </label>
                </div>
              }
              customClass=""
            ></SelectedStickyFooter>
          )}
        </div>
      </div>
    );
  }
}

export default TargetTableView;
